/* eslint-disable react-hooks/exhaustive-deps */
/**
 * TripMapPicker.js — Multi-stop route picker
 * Uses Leaflet + OpenStreetMap (free, no API key).
 * Routing via OSRM public API (free, driving distance).
 * Geocoding via Nominatim (free, text → coordinates).
 *
 * Props:
 *   onRouteChange({ stops, totalDistanceKm, legs, polyline })
 *   stops = [{label, lat, lng}, ...]
 *   legs  = [{from, to, distanceKm}, ...]
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Input, Button, Spin, message, Tooltip } from 'antd';
import {
    AimOutlined, SwapOutlined, CloseCircleOutlined,
    PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined,
} from '@ant-design/icons';

const SZ_CENTER = [-26.5225, 31.4659];
const SZ_ZOOM   = 9;

// Stop label letters: A, B, C, D …
const STOP_LABEL = i => String.fromCharCode(65 + i);

// Stop pin colours
const STOP_COLORS = ['#1a3a5c','#f5222d','#52c41a','#fa8c16','#722ed1','#13c2c2','#eb2f96'];
const stopColor   = i => STOP_COLORS[i % STOP_COLORS.length];

// ── Leaflet CDN loader ────────────────────────────────────────────────────
let leafletLoaded = false, loadPromise = null;
function loadLeaflet() {
    if (leafletLoaded && window.L) return Promise.resolve(window.L);
    if (loadPromise) return loadPromise;
    loadPromise = new Promise((resolve, reject) => {
        if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link');
            link.id = 'leaflet-css'; link.rel = 'stylesheet';
            link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
            document.head.appendChild(link);
        }
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
        script.onload  = () => { leafletLoaded = true; resolve(window.L); };
        script.onerror = () => reject(new Error('Failed to load Leaflet'));
        document.head.appendChild(script);
    });
    return loadPromise;
}

// ── Nominatim geocode ────────────────────────────────────────────────────
async function geocode(query) {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query + ', Eswatini')}&format=json&limit=5&countrycodes=sz`;
    const res  = await fetch(url, { headers: { 'Accept-Language': 'en' } });
    const data = await res.json();
    return data.map(d => ({
        lat:   parseFloat(d.lat),
        lng:   parseFloat(d.lon),
        label: d.display_name,
        short: d.display_name.split(',').slice(0, 2).join(',').trim(),
    }));
}

// ── OSRM routing: array of waypoints → {totalKm, legs, coords} ─────────
async function getRoute(waypoints) {
    if (waypoints.length < 2) return null;
    const coords = waypoints.map(w => `${w.lng},${w.lat}`).join(';');
    const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson&steps=false`;
    const res  = await fetch(url);
    const data = await res.json();
    if (data.code !== 'Ok' || !data.routes?.length) throw new Error('No route found');
    const route = data.routes[0];
    const totalKm = +(route.distance / 1000).toFixed(1);
    // Legs distances
    const legs = (data.routes[0].legs || []).map((leg, i) => ({
        from:       waypoints[i].short,
        to:         waypoints[i + 1].short,
        distanceKm: +(leg.distance / 1000).toFixed(1),
    }));
    // Polyline: GeoJSON [lng,lat] → Leaflet [lat,lng]
    const polyCoords = route.geometry.coordinates.map(c => [c[1], c[0]]);
    return { totalKm, legs, polyCoords };
}

// ── Search box with suggestions ──────────────────────────────────────────
function SearchBox({ placeholder, value, onSelect, onClear, disabled, color }) {
    const [query,       setQuery]       = useState(value?.short || '');
    const [suggestions, setSuggestions] = useState([]);
    const [searching,   setSearching]   = useState(false);
    const debounce = useRef(null);

    useEffect(() => { setQuery(value?.short || ''); }, [value]);

    const search = useCallback((q) => {
        setQuery(q);
        clearTimeout(debounce.current);
        if (q.length < 3) { setSuggestions([]); return; }
        debounce.current = setTimeout(async () => {
            setSearching(true);
            try { setSuggestions(await geocode(q)); }
            catch { setSuggestions([]); }
            setSearching(false);
        }, 500);
    }, []);

    const pick = (item) => { setQuery(item.short); setSuggestions([]); onSelect(item); };
    const clear = () => { setQuery(''); setSuggestions([]); onClear(); };

    return (
        <div style={{ position: 'relative', flex: 1 }}>
            <Input
                prefix={<AimOutlined style={{ color: color || '#888' }}/>}
                suffix={
                    searching ? <Spin size="small"/> :
                    query ? <CloseCircleOutlined style={{ color: '#ccc', cursor: 'pointer' }} onClick={clear}/> : null
                }
                placeholder={placeholder}
                value={query}
                onChange={e => search(e.target.value)}
                disabled={disabled}
                style={{ borderRadius: 6 }}
            />
            {suggestions.length > 0 && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:9999,
                    background:'#fff', border:'1px solid #e8e8e8', borderRadius:6,
                    boxShadow:'0 4px 12px rgba(0,0,0,0.12)', maxHeight:200, overflowY:'auto', marginTop:2 }}>
                    {suggestions.map((s, i) => (
                        <div key={i} onClick={() => pick(s)}
                            style={{ padding:'8px 12px', fontSize:12, cursor:'pointer',
                                borderBottom:'1px solid #f5f5f5', lineHeight:1.4 }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                            {s.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Main TripMapPicker ────────────────────────────────────────────────────
const DEFAULT_STOPS = [
    { short: '', lat: null, lng: null },  // A — origin
    { short: '', lat: null, lng: null },  // B — first destination
];

export default function TripMapPicker({ onRouteChange }) {
    const mapRef        = useRef(null);
    const mapInstance   = useRef(null);
    const markersRef    = useRef([]);
    const routeLines    = useRef([]);
    const [L,           setL]            = useState(null);
    const [loadingMap,  setLoadingMap]   = useState(true);
    const [loadingRoute,setLoadingRoute] = useState(false);
    const [stops,       setStops]        = useState(DEFAULT_STOPS);
    const [clickTarget, setClickTarget]  = useState(null); // index of stop waiting for map click
    const [routeInfo,   setRouteInfo]    = useState(null); // {totalKm, legs}

    // ── Init map ───────────────────────────────────────────────────────────
    useEffect(() => {
        loadLeaflet().then(leaflet => {
            setL(leaflet);
            setLoadingMap(false);
            if (mapRef.current && !mapInstance.current) {
                const map = leaflet.map(mapRef.current, {
                    center: SZ_CENTER, zoom: SZ_ZOOM, zoomControl: true,
                });
                leaflet.tileLayer(
                    'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                    { attribution: '© OpenStreetMap contributors', maxZoom: 19 }
                ).addTo(map);
                mapInstance.current = map;

                map.on('click', e => {
                    setClickTarget(target => {
                        if (target === null) return target;
                        const { lat, lng } = e.latlng;
                        const pt = { short: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, lat, lng };
                        setStops(prev => prev.map((s, i) => i === target ? pt : s));
                        return null;
                    });
                });
            }
        }).catch(() => { setLoadingMap(false); message.error('Map failed to load'); });

        return () => {
            if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }
        };
    }, []);

    // ── Redraw all markers & route whenever stops change ──────────────────
    useEffect(() => {
        if (!L || !mapInstance.current) return;

        // Clear old markers
        markersRef.current.forEach(m => m.remove());
        markersRef.current = [];
        routeLines.current.forEach(l => l.remove());
        routeLines.current = [];

        // Place markers for filled stops
        stops.forEach((s, i) => {
            if (!s.lat) return;
            const color = stopColor(i);
            const icon = L.divIcon({
                className: '',
                html: `<div style="
                    width:26px;height:26px;border-radius:50% 50% 50% 0;
                    background:${color};border:3px solid #fff;
                    box-shadow:0 2px 8px rgba(0,0,0,.3);
                    transform:rotate(-45deg);display:flex;align-items:center;
                    justify-content:center;
                "><span style="transform:rotate(45deg);font-size:10px;font-weight:700;color:#fff">${STOP_LABEL(i)}</span></div>`,
                iconSize: [26, 26], iconAnchor: [13, 26],
            });
            const m = L.marker([s.lat, s.lng], { icon })
                .addTo(mapInstance.current)
                .bindPopup(`<b>Stop ${STOP_LABEL(i)}</b><br/>${s.short}`);
            markersRef.current.push(m);
        });

        // Draw route if 2+ filled stops
        const filled = stops.filter(s => s.lat);
        if (filled.length < 2) {
            setRouteInfo(null);
            onRouteChange && onRouteChange(null);
            return;
        }

        setLoadingRoute(true);
        getRoute(filled).then(result => {
            if (!result) return;
            const { totalKm, legs, polyCoords } = result;

            const line = L.polyline(polyCoords, {
                color: '#1a3a5c', weight: 4, opacity: 0.8,
            }).addTo(mapInstance.current);
            routeLines.current = [line];
            mapInstance.current.fitBounds(line.getBounds(), { padding: [40, 40] });

            setRouteInfo({ totalKm, legs });
            onRouteChange && onRouteChange({
                stops:          filled.map(s => ({ label: s.short, lat: s.lat, lng: s.lng })),
                totalDistanceKm: totalKm,
                legs,
                polyline:        JSON.stringify(polyCoords),
                // convenience fields (first + last stop)
                fromLocation:    filled[0].short,
                fromLat:         filled[0].lat,
                fromLng:         filled[0].lng,
                toLocation:      filled[filled.length - 1].short,
                toLat:           filled[filled.length - 1].lat,
                toLng:           filled[filled.length - 1].lng,
                distanceKm:      totalKm,
            });
        }).catch(err => {
            message.warning('Route unavailable — distance not calculated. (' + err.message + ')');
        }).finally(() => setLoadingRoute(false));

    }, [stops, L]);

    // ── Stop manipulation ─────────────────────────────────────────────────
    const updateStop = (i, val) => setStops(prev => prev.map((s, idx) => idx === i ? val : s));
    const clearStop  = (i) => updateStop(i, { short: '', lat: null, lng: null });

    const addStop = () => {
        if (stops.length >= 7) { message.info('Maximum 7 stops supported'); return; }
        // Insert before the last stop (destination stays last)
        setStops(prev => [
            ...prev.slice(0, prev.length - 1),
            { short: '', lat: null, lng: null },
            prev[prev.length - 1],
        ]);
    };

    const removeStop = (i) => {
        if (stops.length <= 2) return; // always keep at least A and B
        setStops(prev => prev.filter((_, idx) => idx !== i));
    };

    const moveUp   = (i) => {
        if (i === 0) return;
        setStops(prev => { const a = [...prev]; [a[i-1], a[i]] = [a[i], a[i-1]]; return a; });
    };
    const moveDown = (i) => {
        if (i === stops.length - 1) return;
        setStops(prev => { const a = [...prev]; [a[i], a[i+1]] = [a[i+1], a[i]]; return a; });
    };

    const swapFirstLast = () => {
        if (stops.length === 2) {
            setStops(prev => [prev[1], prev[0]]);
        }
    };

    return (
        <div style={{ border:'1px solid #e8e8e8', borderRadius:8, overflow:'hidden', marginBottom:12 }}>
            {/* ── Stop list ── */}
            <div style={{ padding:'10px 12px', background:'#f8f9fa', borderBottom:'1px solid #e8e8e8' }}>
                {stops.map((stop, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                        {/* Stop label badge */}
                        <div style={{
                            width:22, height:22, borderRadius:'50%', flexShrink:0,
                            background: stopColor(i), color:'#fff',
                            fontSize:11, fontWeight:700, display:'flex',
                            alignItems:'center', justifyContent:'center',
                        }}>
                            {STOP_LABEL(i)}
                        </div>

                        {/* Search box */}
                        <SearchBox
                            placeholder={
                                i === 0               ? 'Origin — search or click map' :
                                i === stops.length-1  ? 'Destination — search or click map' :
                                `Stop ${STOP_LABEL(i)} — search or click map`
                            }
                            value={stop}
                            onSelect={v => updateStop(i, v)}
                            onClear={() => clearStop(i)}
                            color={stopColor(i)}
                        />

                        {/* Click-on-map toggle */}
                        <Button size="small" shape="circle"
                            style={{
                                background: clickTarget === i ? stopColor(i) : '#fff',
                                color:      clickTarget === i ? '#fff' : stopColor(i),
                                border: `1px solid ${stopColor(i)}`, flexShrink:0,
                            }}
                            title="Click map to set this stop"
                            onClick={() => setClickTarget(t => t === i ? null : i)}>
                            <AimOutlined/>
                        </Button>

                        {/* Reorder / remove (only for non A/B or when 3+ stops) */}
                        {stops.length > 2 && (
                            <div style={{ display:'flex', gap:2, flexShrink:0 }}>
                                <Tooltip title="Move up">
                                    <Button size="small" shape="circle" icon={<ArrowUpOutlined/>}
                                        disabled={i === 0}
                                        onClick={() => moveUp(i)}
                                        style={{ fontSize:10 }}/>
                                </Tooltip>
                                <Tooltip title="Move down">
                                    <Button size="small" shape="circle" icon={<ArrowDownOutlined/>}
                                        disabled={i === stops.length - 1}
                                        onClick={() => moveDown(i)}
                                        style={{ fontSize:10 }}/>
                                </Tooltip>
                                <Tooltip title="Remove stop">
                                    <Button size="small" shape="circle" danger
                                        icon={<DeleteOutlined/>}
                                        onClick={() => removeStop(i)}
                                        style={{ fontSize:10 }}/>
                                </Tooltip>
                            </div>
                        )}
                    </div>
                ))}

                {/* Add stop + swap + distance bar */}
                <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
                    <Button size="small" icon={<PlusOutlined/>} onClick={addStop}
                        disabled={stops.length >= 7}
                        style={{ fontSize:11 }}>
                        Add stop
                    </Button>
                    {stops.length === 2 && (
                        <Button size="small" icon={<SwapOutlined/>} onClick={swapFirstLast}
                            disabled={!stops[0].lat && !stops[1].lat}
                            style={{ fontSize:11 }}>
                            Swap
                        </Button>
                    )}
                    <div style={{ flex:1 }}/>
                    {loadingRoute && (
                        <span style={{ fontSize:12, color:'#888' }}>
                            <Spin size="small" style={{ marginRight:6 }}/>
                            Calculating route…
                        </span>
                    )}
                    {!loadingRoute && routeInfo && (
                        <div style={{
                            background:'#e6f4ff', border:'1px solid #91caff',
                            borderRadius:6, padding:'4px 12px',
                            fontSize:13, fontWeight:600, color:'#003eb3',
                        }}>
                            Total: {routeInfo.totalKm} km
                            {routeInfo.legs.length > 1 && (
                                <span style={{ fontSize:10, fontWeight:400, color:'#2e86c1', marginLeft:8 }}>
                                    ({routeInfo.legs.length} legs)
                                </span>
                            )}
                        </div>
                    )}
                    {clickTarget !== null && (
                        <div style={{
                            fontSize:11, color: stopColor(clickTarget),
                            background: '#f0f4ff', border:`1px solid ${stopColor(clickTarget)}`,
                            borderRadius:6, padding:'4px 10px',
                        }}>
                            Click map → Stop {STOP_LABEL(clickTarget)}
                        </div>
                    )}
                </div>

                {/* Leg breakdown (multi-stop) */}
                {routeInfo && routeInfo.legs.length > 1 && (
                    <div style={{
                        marginTop:8, background:'#fff', border:'1px solid #e8e8e8',
                        borderRadius:6, padding:'6px 10px', fontSize:11,
                    }}>
                        {routeInfo.legs.map((leg, i) => (
                            <div key={i} style={{ display:'flex', gap:8, padding:'2px 0',
                                borderBottom: i < routeInfo.legs.length-1 ? '1px solid #f5f5f5' : 'none',
                                color:'#555' }}>
                                <span style={{ fontWeight:600, color: stopColor(i) }}>
                                    {STOP_LABEL(i)} → {STOP_LABEL(i+1)}
                                </span>
                                <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                    {leg.from} → {leg.to}
                                </span>
                                <span style={{ fontWeight:600, color:'#003eb3', flexShrink:0 }}>
                                    {leg.distanceKm} km
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Map ── */}
            <div style={{ position:'relative' }}>
                {loadingMap && (
                    <div style={{ position:'absolute', inset:0, display:'flex',
                        alignItems:'center', justifyContent:'center',
                        background:'#f0f4f8', zIndex:10, height:360 }}>
                        <Spin tip="Loading map…"/>
                    </div>
                )}
                <div ref={mapRef} style={{ height:360, width:'100%' }}/>
            </div>

            {/* Attribution */}
            <div style={{ padding:'4px 10px', fontSize:10, color:'#aaa',
                background:'#fafafa', borderTop:'1px solid #f0f0f0' }}>
                Map: © OpenStreetMap contributors · Routing: OSRM
            </div>
        </div>
    );
}